/**
 * ActionTable — table view for homogeneous CRM action groups.
 *
 * Built on the shared `Table` / `TableCard` primitives from
 * `@/lib/base/table` so the proposal table stays visually consistent with
 * the rest of the app's data tables. The action-specific wiring
 * (chevron / checkbox / avatar / status columns, property vs association
 * cells, and the inline "click a row to expand the action timeline"
 * pattern) lives in `./ActionTable.helpers.tsx`.
 */

import { useMemo } from 'react';

import { Table, TableCard } from '@/lib/base/table';
import type {
  PropertySchemaMap,
  PropertyTypeSchema,
} from '@/lib/crm/hooks/usePropertySchema';
import Pagination from '@/lib/proposals/Pagination';
import type { PropertyValue } from '@/types/crm';
import { getPropertyValue, unwrapPropertyValue } from '@/types/crm';

import type {
  ProposalDetail,
  ProposedAction,
} from '../../lib/proposals/core/types';
import { isObjectCreatedPayload } from '../../lib/proposals/core/types';
import type { SelectionState } from '../../lib/proposals/types/selection';
import { ACTION_TABLE_PAGE_SIZE } from '../../lib/proposals/utils/constants';
import { getStableActionKey } from '../../lib/proposals/utils/helpers';
import {
  ActionDataRow,
  ActionExpandedRow,
  ActionTableColumnHeaders,
  formatPropertyName,
} from './ActionTable.helpers';
import type { ColumnDef } from './ActionTable.types';
import {
  useTablePaginationState,
  useTableSelectionHandlers,
} from './tableHooks';

interface ActionTableProps {
  actions: ProposedAction[];
  /**
   * Ordered list of columns to render. Mixes property columns (sourced from
   * the CRM store or action payload) and association columns (sourced from
   * the CRM store's associations — each renders the target entity's display
   * name, e.g. a contact's "Works at" company).
   */
  columns: ColumnDef[];
  onUpdate: (proposal: ProposalDetail) => void;
  isProcessing?: boolean;
  selectionState?: SelectionState;
  proposal?: ProposalDetail;
  entityTypeLabel?: string;
  /**
   * Combined label map for both property (`usePropertySchema`) and
   * association (`useAssociationTypes`) columns, keyed by each column's
   * `key`.
   */
  columnLabels?: Record<string, string>;
  propertySchema?: PropertySchemaMap;
  fillContainer?: boolean;
}

export function ActionTable({
  actions,
  columns,
  onUpdate,
  isProcessing = false,
  selectionState,
  proposal,
  entityTypeLabel,
  columnLabels,
  propertySchema,
  fillContainer = false,
}: ActionTableProps) {
  // Pull out just the property keys for the "is this column populated" and
  // "schema-per-column" computations below.
  const propertyKeys = useMemo(
    () => columns.filter(c => c.kind === 'property').map(c => c.key),
    [columns]
  );
  const {
    currentPage,
    paginatedActions,
    expandedActionKey,
    handlePageChange,
    handleRowClick,
  } = useTablePaginationState(actions);

  // Filter property keys to only those where at least one create action has
  // a value in its payload.
  const populatedPropertyKeys = useMemo(() => {
    return propertyKeys.filter(key => {
      for (const action of actions) {
        if (isObjectCreatedPayload(action.payload)) {
          const val =
            unwrapPropertyValue(action.payload.payload.properties[key]) ?? null;
          if (val !== null && val !== undefined && val !== '') {
            return true;
          }
        }
      }
      return false;
    });
  }, [propertyKeys, actions]);

  // Ordered, populated columns for the row / header to render. Preserves
  // the original detailFieldOrder ordering by walking the full `columns`
  // list and keeping each property column with at least one populated value.
  // Rows can't supply association values from the action payload, so
  // association columns would only produce placeholder cells that displace
  // real property fields below MAX_TABLE_COLUMNS.
  const displayedColumns = useMemo<ColumnDef[]>(() => {
    const populatedSet = new Set(populatedPropertyKeys);
    return columns.filter(c => populatedSet.has(c.key));
  }, [columns, populatedPropertyKeys]);

  const hasEmailProperty = populatedPropertyKeys.includes('ahoy_email');
  const hasSelectionState = !!selectionState;

  // Single-pass O(N+M) algorithm: first find all keys with array values (O(N)),
  // then build the schema map with is_multi overrides (O(M)).
  const effectiveSchemaMap = useMemo(() => {
    const map: Record<string, PropertyTypeSchema | undefined> = {};
    const keysWithArrayValues = new Set<string>();
    for (const action of actions) {
      if (!isObjectCreatedPayload(action.payload)) {
        continue;
      }
      for (const key of propertyKeys) {
        if (keysWithArrayValues.has(key)) {
          continue;
        }
        const wrapper = action.payload.payload.properties[key];
        // proposal payload action.payload.payload.properties[key] is a PropertyValue {value} — unwrap via getPropertyValue
        const value = getPropertyValue<unknown>(wrapper as PropertyValue);
        if (Array.isArray(value)) {
          keysWithArrayValues.add(key);
        }
      }
    }
    for (const key of propertyKeys) {
      const propSchema = propertySchema?.[key];
      if (keysWithArrayValues.has(key) && !propSchema?.is_multi) {
        map[key] = propSchema
          ? { ...propSchema, is_multi: true }
          : {
              property_type_name: key,
              entity_type_name: '',
              label: formatPropertyName(key),
              field_type_name: key === 'ahoy_email' ? 'email' : 'text',
              description: '',
              is_required: false,
              is_unique_value: false,
              is_multi: true,
              value_write_access: 'all' as const,
              value_read_access: 'all' as const,
              value_update_access: 'all' as const,
            };
      } else {
        map[key] = propSchema;
      }
    }
    return map;
  }, [propertyKeys, propertySchema, actions]);

  const { handleToggle, handleHeaderCheckboxChange } =
    useTableSelectionHandlers(selectionState);

  const tableRows = useMemo(() => {
    const rows: Array<{
      type: 'data' | 'expanded';
      action: ProposedAction;
      id: string;
    }> = [];
    // Row ids become React keys, so they must survive a save. See
    // getStableActionKey — a save past the versioning window mints a new
    // action.id, and keying on it would remount the row (and any editor
    // inside the expanded row) mid-edit.
    for (const action of paginatedActions) {
      const stableId = getStableActionKey(action);
      rows.push({ type: 'data', action, id: String(stableId) });
      if (expandedActionKey === stableId && proposal) {
        rows.push({ type: 'expanded', action, id: `${stableId}-expanded` });
      }
    }
    return rows;
  }, [paginatedActions, expandedActionKey, proposal]);

  if (actions.length === 0) {
    return (
      <div className='p-6 text-center text-sm text-tertiary'>
        No actions to display
      </div>
    );
  }

  const showPagination = actions.length > ACTION_TABLE_PAGE_SIZE;

  const totalColumns =
    1 +
    (hasSelectionState ? 1 : 0) +
    (hasEmailProperty ? 1 : 0) +
    1 +
    displayedColumns.length;

  const headerDescription = 'Will be created when approved';

  return (
    <TableCard.Root size='sm' fillContainer={fillContainer}>
      {entityTypeLabel && (
        <TableCard.Header
          title={`${actions.length} ${entityTypeLabel}`}
          description={headerDescription}
        />
      )}
      <TableCard.ScrollArea>
        <Table aria-label={entityTypeLabel || 'Actions'} size='sm'>
          <ActionTableColumnHeaders
            selectionState={selectionState}
            hasEmailProperty={hasEmailProperty}
            columns={displayedColumns}
            columnLabels={columnLabels}
            onHeaderCheckboxChange={handleHeaderCheckboxChange}
          />
          <Table.Body>
            {tableRows.map(row => {
              const { action, type } = row;
              if (type === 'expanded' && proposal) {
                return (
                  <ActionExpandedRow
                    key={row.id}
                    action={action}
                    totalColumns={totalColumns}
                    proposal={proposal}
                    onUpdate={onUpdate}
                    isProcessing={isProcessing}
                  />
                );
              }
              const isSelected = selectionState?.isSelected(action.id) ?? true;
              const isDisabled = selectionState?.isDisabled(action.id) ?? false;
              const isExpanded =
                expandedActionKey === getStableActionKey(action);
              return (
                <ActionDataRow
                  key={row.id}
                  action={action}
                  isSelected={isSelected}
                  isDisabled={isDisabled}
                  isExpanded={isExpanded}
                  hasEmailProperty={hasEmailProperty}
                  hasSelectionState={hasSelectionState}
                  columns={displayedColumns}
                  effectiveSchemaMap={effectiveSchemaMap}
                  onToggle={handleToggle}
                  onRowClick={handleRowClick}
                />
              );
            })}
          </Table.Body>
        </Table>
      </TableCard.ScrollArea>
      {showPagination && (
        <TableCard.Footer aria-label='Table pagination' role='navigation'>
          <Pagination
            total={actions.length}
            pageSize={ACTION_TABLE_PAGE_SIZE}
            page={currentPage}
            onPageChange={handlePageChange}
            className='!border-t-0'
          />
        </TableCard.Footer>
      )}
    </TableCard.Root>
  );
}
